import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { QRCodeCanvas } from "qrcode.react";
import ProfileService from "../../services/ProfileService";
import PPIService from "../../services/PPIService";

Modal.setAppElement("#root");

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    padding: "30px",
    maxWidth: "500px",
    width: "100%",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
};

const AddMoneyModal = ({ isOpen, onRequestClose, cardBalance }) => {
  const [amount, setAmount] = useState("");
  const [orderId, setOrderId] = useState("");
  const [upiUrl, setUpiUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180);
  const timerRef = useRef(null);
  const intervalRef = useRef(null);

  const handleAddMoney = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      return Swal.fire("Oops!", "Please enter a valid amount", "error");
    }

    setLoading(true);
    try {
      let result;

      if (source == "api") {
        result = await PPIService.cardReload(amount);
      } else {
        result = await ProfileService.getUPIQR(amount, "PPI_TOPUP");
      }

      if (!result.success) {
        return Swal.fire("Oops!", result.message || "Failed to fetch", "error");
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        const data = responseData.data;
        setUpiUrl(data.upi_intent);
        setOrderId(data.order_id);
        setTimeLeft(180);

        timerRef.current = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              clearInterval(intervalRef.current);
              setUpiUrl("");
              setOrderId("");
              Swal.fire(
                "QR Expired",
                "Please try again to get a new QR code",
                "info"
              );
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || "Request failed",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const txnStatus = async () => {
    if (!orderId) return;
    try {
      let result;

      if (source == "api") {
        result = await PPIService.cardReloadStatus(orderId);
      } else {
        result = await ProfileService.txnStatus(orderId);
      }

      if (result.success && result.data.status === "SUCCESS") {
        Swal.fire(
          "Success!",
          result.data.message || "Money added successfully",
          "success"
        );
        closeHandler();
      }
    } catch (error) {
      console.error("Transaction status check failed:", error);
    }
  };

  const [source, setSource] = useState("");

  // Start polling when both upiUrl and orderId are set
  useEffect(() => {
    if (upiUrl && orderId) {
      intervalRef.current = setInterval(() => {
        txnStatus();
      }, 15000);
    }

    const source = localStorage.getItem("source");
    setSource(source);

    return () => clearInterval(intervalRef.current);
  }, [upiUrl, orderId]);

  const closeHandler = () => {
    setAmount("");
    setUpiUrl("");
    setOrderId("");
    setTimeLeft(180);
    clearInterval(timerRef.current);
    clearInterval(intervalRef.current);
    onRequestClose();
  };

  const formatTime = (secs) => {
    const minutes = Math.floor(secs / 60);
    const seconds = secs % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  useEffect(() => {
    if (!isOpen) {
      clearInterval(timerRef.current);
      clearInterval(intervalRef.current);
    }
    return () => {
      clearInterval(timerRef.current);
      clearInterval(intervalRef.current);
    };
  }, [isOpen]);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const styleSheet = document.styleSheets[0];
    const keyframes = `@keyframes glowEffect {
      0% { box-shadow: 0 0 5px #0d6efd; }
      50% { box-shadow: 0 0 20px #0d6efd; }
      100% { box-shadow: 0 0 5px #0d6efd; }
    }`;

    if (styleSheet?.insertRule) {
      try {
        styleSheet.insertRule(keyframes, styleSheet.cssRules.length);
      } catch (err) {
        // Avoid duplicate rule insertion error
      }
    }
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeHandler}
      style={customStyles}
      contentLabel="Add Money"
    >
      <div>
        <h4 className="mb-3 text-center text-primary fw-bold">
          Add Money to Card
        </h4>

        <div className="mb-4 d-flex gap-3">
          <div className="card flex-fill text-center shadow-sm border-0">
            <div className="card-body">
              <i className="bi bi-wallet2 fs-3 text-primary mb-2"></i>
              <h6 className="card-title mb-1">Card Balance</h6>
              <p className="card-text fw-bold">
                ₹{cardBalance?.available_balance || "0.00"}
              </p>
            </div>
          </div>
          <div className="card flex-fill text-center shadow-sm border-0">
            <div className="card-body">
              <i className="bi bi-arrow-down-up fs-3 text-success mb-2"></i>
              <h6 className="card-title mb-1">Monthly Reload Limit</h6>
              <p className="card-text fw-bold">
                ₹{cardBalance?.monthly_reloadable_amount || "0.00"}
              </p>
            </div>
          </div>
        </div>

        {!upiUrl ? (
          <>
            <div className="mb-3">
              <label className="form-label">Enter Amount</label>
              <input
                type="number"
                className="form-control"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 500"
              />
            </div>

            <button
              className="btn btn-success w-100"
              onClick={handleAddMoney}
              disabled={loading}
            >
              {loading ? "Processing..." : "Generate QR to Pay"}
            </button>
          </>
        ) : (
          <div className="text-center">
            <h6 className="fw-semibold mb-3">Scan this QR code to Add Money</h6>
            <QRCodeCanvas value={upiUrl} size={200} />
            <p className="mt-2 text-danger fw-bold">
              QR expires in: {formatTime(timeLeft)}
            </p>
            <p className="text-muted small">Scan & Pay with any BHIM UPI App</p>

            {isMobile && upiUrl && (
              <a
                href={upiUrl}
                className="btn btn-success w-100 mt-3"
                style={{
                  fontWeight: "600",
                  color: "#fff",
                  backgroundColor: "#28a745",
                  borderColor: "#28a745",
                  boxShadow: "0 0 10px #28a745, 0 0 20px #28a745",
                  animation: "glowEffect 1.5s infinite alternate",
                }}
              >
                Pay via UPI App
              </a>
            )}
          </div>
        )}

        <div className="text-end mt-4">
          <button className="btn btn-outline-secondary" onClick={closeHandler}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddMoneyModal;
